import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { WATCH_LABELS, WORK_LABELS, CLOSE_LABELS } from '../constants/bwofWorkflow';
import { InfoButton } from './InfoModal';
import { ChevronRight, RotateCcw } from 'lucide-react';

function StepPill({ label, active, done }) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
        active ? 'border-primary-500 bg-primary-50 font-medium' : done ? 'border-green-300 bg-green-100 text-green-800' : 'border-green-300 bg-green-100 text-gray-500'
      }`}
    >
      <span className={`w-2 h-2 rounded-full ${active ? 'bg-primary-500' : done ? 'bg-green-500' : 'bg-gray-300'}`} />
      {label}
    </div>
  );
}

/**
 * Single building progress panel:
 * Watch / Work / Close summary. SS rows stay on the building Inspections list.
 */
export default function BwofWorkflowPanel({
  buildingId,
  canManage = false,
  canCompleteCycle = false,
  compact = false,
  ssProgressRows = null,
}) {
  const queryClient = useQueryClient();

  const { data: wf, isLoading } = useQuery({
    queryKey: ['bwof-workflow', buildingId],
    queryFn: async () => (await api.get(`/bwof-workflow/buildings/${buildingId}`)).data?.data,
    enabled: !!buildingId,
  });

  const advanceMutation = useMutation({
    mutationFn: () => api.post(`/bwof-workflow/buildings/${buildingId}/advance-close`),
    onSuccess: () => {
      toast.success('Council step advanced');
      queryClient.invalidateQueries({ queryKey: ['bwof-workflow', buildingId] });
      queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
      queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'ss-progress'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to advance'),
  });

  const completeCycleMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/buildings/${buildingId}/complete-bwof-cycle`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'BWOF cycle completed ??dates advanced');
      queryClient.invalidateQueries({ queryKey: ['bwof-workflow', buildingId] });
      queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      queryClient.invalidateQueries({ queryKey: ['building', buildingId, 'ss-progress'] });
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to complete BWOF cycle'),
  });

  if (isLoading || !wf) return null;

  const progressRows = Array.isArray(ssProgressRows) ? ssProgressRows : null;
  const useFullProgress = progressRows && progressRows.length > 0;
  const showCompleteCycle =
    canManage &&
    canCompleteCycle &&
    !compact &&
    wf.closeStep === 'approved';

  return (
    <div
      id="ss-progress"
      className={`bg-card rounded-lg shadow border border-gray-200 scroll-mt-4 ${compact ? 'p-4 mb-4' : 'p-6 mb-6'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-xl font-semibold text-gray-900">BWOF progress</h2>
          <InfoButton title="BWOF progress">
            <p>
              <strong>Watch</strong> is the building BWOF due date. <strong>Work</strong> means an SS inspection
              is still open. <strong>Close</strong> is the council pack, lodge, and approval. Each SS stays on
              the building Inspections list.
            </p>
          </InfoButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="p-3 rounded-lg bg-green-100 border border-green-300">
          <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Watch</p>
          <p className="text-sm font-medium">{WATCH_LABELS[wf.watchStep] || '—'}</p>
          <p className="text-xs text-slate-600 mt-1">
            Due {wf.due_date ? new Date(wf.due_date).toLocaleDateString() : '—'}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
          <p className="text-xs font-semibold text-blue-600 uppercase mb-2">Work</p>
          <p className="text-sm font-medium">{WORK_LABELS[wf.workStep] || '—'}</p>
          <p className="text-xs text-blue-700 mt-1">
            {useFullProgress
              ? `${progressRows.length} SS row(s)`
              : `${wf.jobs?.length || 0} SS inspection(s) tracked`}
          </p>
        </div>
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
          <p className="text-xs font-semibold text-emerald-700 uppercase mb-2">Close</p>
          <p className="text-sm font-medium">
            {wf.closeStep ? CLOSE_LABELS[wf.closeStep] : wf.workStep === 'all_jobs_done' ? 'Ready to start' : '—'}
          </p>
          {wf.application_approved && (
            <p className="text-xs text-emerald-700 mt-1">Council approved</p>
          )}
        </div>
      </div>

      {wf.phase === 'CLOSE' && wf.close_steps?.length > 0 && (
        <div className="mb-2">
          <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Council submission (Close)</p>
          <div className="flex flex-wrap gap-2">
            {wf.close_steps.map((step) => (
              <StepPill key={step.id} label={step.label} active={step.current} done={step.done} />
            ))}
          </div>
          {canManage && wf.closeStep && wf.closeStep !== 'approved' && (
            <button
              type="button"
              onClick={() => {
                if (
                  !window.confirm(
                    'Advance council Close to the next step? Confirm only when this step is done.'
                  )
                ) {
                  return;
                }
                advanceMutation.mutate();
              }}
              disabled={advanceMutation.isPending}
              className="mt-3 flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              <ChevronRight className="w-4 h-4" />
              Confirm ??advance to next step
            </button>
          )}
        </div>
      )}

      {showCompleteCycle && (
        <div className="mt-4 p-4 rounded-lg border border-emerald-200 bg-emerald-50">
          <p className="text-sm font-medium text-emerald-900">Council approved ??close this BWOF year</p>
          <p className="text-xs text-emerald-800 mt-1 mb-3">
            Rolls CS issue date and BWOF due date to the next period. Use only after the certificate is issued
            for this cycle.
            {!wf.jobs_complete && (
              <span className="block mt-1 text-amber-800">
                Note: some SS inspections are still open ??confirm before advancing dates.
              </span>
            )}
          </p>
          <button
            type="button"
            onClick={() => {
              if (
                !window.confirm(
                  'Complete this BWOF cycle and advance anniversary / due dates to the next period?'
                )
              ) {
                return;
              }
              completeCycleMutation.mutate();
            }}
            disabled={completeCycleMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-700 text-white rounded-lg hover:bg-emerald-800 disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            {completeCycleMutation.isPending ? 'Advancing…' : 'Complete BWOF cycle'}
          </button>
        </div>
      )}
    </div>
  );
}
